import { BillingType } from 'domain/shared/newProject';
import { SCHEDULE_ITEM_TYPE } from 'domain/projects/newProject/billing/constant';
import {
  BillingRequestItem,
  BillingRequestSearchResultItem,
  BillingRequestStatus,
  BillingScheduleItemResult,
  CreateBillingRequestPayload,
  UpdateBilledItem,
  UpdateBillingRequestPayload,
} from '../billingService';
import { formatChangeRequestAlias, formatCustomPeriod, formatPeriod, parseAmount } from '../billingUtils';
import {
  CreateBillingItemWithPrepaidHours,
  CreateBillingRequestFormValues,
  UpdateBillingRequestFormValues,
  UpdateBillingRequestSearchResultWithPrepaidHours,
} from './types';
import { cloneDeep } from 'lodash';

export const MAX_SIZE_ARTIFACT = 8388608;

type BillingFormItem = BillingScheduleItemResult & {
  name?: string;
  changeRequestAlias?: string | null;
};

export const isPeriodicBillingType = (type: string): boolean =>
  type === BillingType.PERIODIC_PREPAID ||
  type === BillingType.PERIODIC_POSTPAID ||
  type === SCHEDULE_ITEM_TYPE.CALENDAR_PERIOD ||
  type === SCHEDULE_ITEM_TYPE.CUSTOM_PERIOD;

export const getPeriodDisplayValue = (item: BillingFormItem, isUpdate: boolean): string => {
  if (item.changeRequestAlias) {
    return formatChangeRequestAlias(item.changeRequestAlias);
  }

  const isCustomPeriod = item.type === SCHEDULE_ITEM_TYPE.CUSTOM_PERIOD;

  if (isUpdate) {
    return isCustomPeriod ? formatCustomPeriod(item.startDate, item.endDate) : item.name || '';
  }

  if (isCustomPeriod) {
    return formatCustomPeriod(item.startDate, item.endDate);
  }

  return formatPeriod(item.position, item.periodMonth);
};

export const getItemValue = (item: BillingFormItem, isUpdate: boolean, isPeriodic: boolean): string => {
  if (isPeriodic) {
    return getPeriodDisplayValue(item, isUpdate);
  }
  return isUpdate ? item.name || '' : item.milestoneName || '';
};

export const getItemSource = (index: number, isUpdate: boolean, isPeriodic: boolean): string => {
  if (isUpdate) {
    return `data.${index}.name`;
  }
  return isPeriodic ? `data.${index}.periodMonth` : `data.${index}.milestoneName`;
};

export const calculateTotal = (items: BillingScheduleItemResult[]) => {
  const total = items.reduce((sum, item) => {
    const amount = typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount;
    return sum + (amount || 0);
  }, 0);
  const currency = items[0]?.currency || '';
  return { totalAmount: total, currency };
};

const sortByPosition = (a: { position?: string }, b: { position?: string }): number =>
  Number(a.position || 0) - Number(b.position || 0);

export const prepareCreateBillingFormData = (items: CreateBillingItemWithPrepaidHours[], billingType?: BillingType) => {
  const sortedItems = [...items].sort(sortByPosition);
  const itemsWithFormId = sortedItems.map((item, index) => ({
    ...cloneDeep(item),
    formItemId: `${item.id}-${index}`,
    ...(billingType === BillingType.PERIODIC_PREPAID && {
      plannedRegularHours: item.plannedRegularHours ?? item.regularHours,
      plannedOvertimeHours: item.plannedOvertimeHours ?? item.overtimeHours,
      previousPeriodAdjustmentHours: item.previousPeriodAdjustmentHours ?? item.previousAdjustmentHours,
    }),
  }));

  return {
    data: itemsWithFormId,
    type: sortedItems[0]?.type,
    billingType,
  };
};

export const createCreateBillingBasePayload = (
  values: CreateBillingRequestFormValues,
): Omit<CreateBillingRequestPayload, 'billedItems'> => {
  const isDraft = Boolean(values.isDraft);
  const status = isDraft ? BillingRequestStatus.DRAFT : BillingRequestStatus.IN_APPROVAL;
  const serviceDetailsArtifactId = Array.isArray(values.serviceDetailsArtifactId)
    ? values.serviceDetailsArtifactId[0]
    : values.serviceDetailsArtifactId;

  return {
    note: values.note || '',
    serviceDetailsArtifactId,
    reimbursementDocumentationArtifactIds: values.reimbursementDocumentationArtifactIds || [],
    startDate: values.startDate,
    endDate: values.endDate,
    status,
  };
};

const mapToUpdateBilledItems = (
  data: BillingRequestItem[] = [],
  withHours: boolean,
  isPrepaid: boolean,
): UpdateBilledItem[] =>
  data.map(item => {
    const plannedRegularHours = item.plannedRegularHours ?? undefined;
    const plannedOvertimeHours = item.plannedOvertimeHours ?? undefined;
    const previousPeriodAdjustmentHours = item.previousPeriodAdjustmentHours ?? undefined;
    const prepaidTotalHours =
      Number(plannedRegularHours || 0) + Number(plannedOvertimeHours || 0) + Number(previousPeriodAdjustmentHours || 0);

    return {
      id: item.id,
      scheduleItemId: 'scheduleItemId' in item ? (item.scheduleItemId as string) : '',
      name: 'periodMonth' in item ? (item.periodMonth as string) : item.name,
      amount: parseAmount(item.amount),
      currency: item.currency,
      ...(withHours && { hours: item.hours || 0 }),
      ...(isPrepaid && {
        plannedRegularHours,
        plannedOvertimeHours,
        previousPeriodAdjustmentHours,
        hours: prepaidTotalHours,
      }),
    };
  });

export const prepareUpdateBillingFormData = (
  billingRequest: BillingRequestSearchResultItem | UpdateBillingRequestSearchResultWithPrepaidHours,
): UpdateBillingRequestFormValues => {
  const casted = billingRequest as UpdateBillingRequestSearchResultWithPrepaidHours;
  const serviceDetailsArtifactId = casted.serviceDetailsArtifactId ? [casted.serviceDetailsArtifactId] : undefined;

  return {
    data: casted.items.map((item, index: number) => {
      const normalized = {
        ...item,
        plannedRegularHours: item.plannedRegularHours ?? item.regularHours,
        plannedOvertimeHours: item.plannedOvertimeHours ?? item.overtimeHours,
        previousPeriodAdjustmentHours: item.previousPeriodAdjustmentHours ?? item.previousAdjustmentHours,
      };
      return {
        ...normalized,
        formItemId: `${item.id}-${index}`,
        milestoneName: item.name,
      };
    }),
    note: casted.note,
    serviceDetailsArtifactId,
    reimbursementDocumentationArtifactIds: casted.reimbursementDocumentationArtifactIds || [],
    startDate: casted.startDate || undefined,
    endDate: casted.endDate || undefined,
    paidOn: casted.paidOn,
    rejectionReason: casted.rejectionReason,
    status: casted.status,
    type: casted.billingType,
    billingType: casted.billingType,
  };
};

export const createUpdateBillingPayload = (
  values: UpdateBillingRequestFormValues & { billingType?: BillingType },
): UpdateBillingRequestPayload => {
  const isDraft = Boolean(values.isDraft);
  const status = isDraft ? BillingRequestStatus.DRAFT : BillingRequestStatus.IN_APPROVAL;
  const billingType = values.billingType || values.type;
  const isPeriodic = isPeriodicBillingType(values.type || '');
  const isPrepaid = billingType === BillingType.PERIODIC_PREPAID;
  const includeHours = isPeriodic && billingType !== BillingType.PERIODIC_PREPAID;
  const serviceDetailsArtifactId = Array.isArray(values.serviceDetailsArtifactId)
    ? values.serviceDetailsArtifactId[0]
    : values.serviceDetailsArtifactId;

  return {
    note: values.note || '',
    serviceDetailsArtifactId,
    reimbursementDocumentationArtifactIds: values.reimbursementDocumentationArtifactIds || [],
    billedItems: mapToUpdateBilledItems(values.data, includeHours, isPrepaid),
    startDate: values.startDate,
    endDate: values.endDate,
    status,
    paidOn: values.paidOn || null,
    rejectionReason: values.rejectionReason || null,
  };
};
